import { ElementRef, Injectable } from '@angular/core';
import domToImage from 'dom-to-image-more';
import { LL } from 'i18n/i18n.service';
import { sleep } from '@cf-shared/shared-util';
import { SendFeedbackType } from '@cf-shared/send-feedback-interface';
import { ToastType } from 'components/toast/toast-host';
import { ISendFeedbackPage } from 'pages/send-feedback/send-feedback-interface';
import { NavController } from 'providers/nav-controller';
import { readSafeAreaInset } from 'services/safe-area-overlap-monitor.service';
import { ToastController } from 'providers/toast-controller';
import { TutorialController } from 'providers/tutorial-controller';
import { LoggingService, errorReportFields } from 'services/logging.service';
import { ScreenshotTrip } from 'services/screenshot-trip';
import { ScrollHideService } from 'services/scroll-hide.service';
import { SettingsService } from 'services/settings.service';
import { buildBugReportCaptureOptions } from 'util/bug-report-capture-options';
import { clampBugFabPosition, hasBugFabDragStarted, shouldShowReportBugFab } from 'services/bug-report-fab';

/**
 * The two elements the button needs that belong to `AppComponent`'s template, so
 * they stay `@ViewChild`s there. `AppComponent` passes this in when it initializes
 * the button.
 */
export interface BugReportFabHost {
  /**
   * Read as a function on purpose, NOT as a value. The button sits inside an
   * `@if`, so its element comes and goes; `reportBugButtonClicked` switches it on,
   * waits one tick, and only then reads it.
   */
  getFabElement(): ElementRef<HTMLElement> | undefined;
  /** The element the screenshot is drawn from: the whole split pane. */
  getCaptureRoot(): HTMLElement;
}

/**
 * The floating "report a bug" camera button: showing and hiding it, dragging it,
 * and capturing the screenshot when it is tapped (fifth slice of deferred finding
 * df-7b66acbeb341).
 *
 * Lifted out of `AppComponent` with no behavior change. The root template
 * (`app.html`) binds to this service directly. The pages that start a screenshot
 * trip still reach it through `getMyApp()`, whose `IMyApp` members on the
 * component forward here. The decisions — when the button shows, when a gesture
 * is a drag, how far it may go — live in `bug-report-fab.ts`, which has no
 * Angular in it and is unit tested.
 */
@Injectable({ providedIn: 'root' })
export class BugReportFabService {
  /** The "leave the page to take a screenshot, then come back" round trip. See its own file. */
  readonly screenshotTrip = new ScreenshotTrip();
  sendFeedbackType: SendFeedbackType;

  private dragging = false;
  private moved = false; // used to distinguish between click and drag
  private offsetX = 0;
  private offsetY = 0;
  // the status bar and navigation bar heights, read once per drag: they cannot change mid-drag
  private dragInsets = { top: 0, bottom: 0 };
  private dragOrgX = 0;
  private dragOrgY = 0;

  private host: BugReportFabHost;

  constructor(
    private navCtrl: NavController,
    private settings: SettingsService,
    private tutorialCtrl: TutorialController,
    private scrollHideService: ScrollHideService,
    private toastCtrl: ToastController,
    private logging: LoggingService,
  ) { }

  /** Called once, from `AppComponent`'s constructor. */
  initialize(host: BugReportFabHost): void {
    this.host = host;
  }

  private get fab(): ElementRef<HTMLElement> | undefined {
    return this.host.getFabElement();
  }

  // when the toggle in the menu is used to show/hide the report bug button
  showScreenshotButton(feedbackType: SendFeedbackType) {
    this.sendFeedbackType = feedbackType;
    this.settings.showReportBugFAB2 = true;
    // display the tutorial
    void this.tutorialCtrl.playTutorialAsync({
      id: 'ALWAYS',
      steps: [
        {
          message: LL.app.screenshotHelp(),
          highlightSelectors: ['cs-fab.report-bug-fab'],
          highlightScaler: 1.5,
        }
      ]
    })
  }

  hideScreenshotButton() {
    this.settings.showReportBugFAB2 = false;
    // hiding the button abandons the screenshot trip, so the stashed text and the page
    // it was going to return to both go with it. Why the return page has to go too, and
    // why a captured image deliberately does not, are explained on `abandon` itself
    this.screenshotTrip.abandon();
  }

  /** The "noticed something unusual?" toast: report it as an issue, tagged with its Sentry event. */
  reportIssueFromToastAsync(sentryId: string | undefined): Promise<void> {
    this.sendFeedbackType = SendFeedbackType.Issue;
    return this.reportBugButtonClicked(sentryId);
  }

  async reportBugButtonClicked(sentryId?: string) {
    // if the button is not visible (happens when the user clicks on "noticed something unusual?" toast)
    const fabDidntExist = !this.fab || !this.fab.nativeElement;
    if (fabDidntExist) {
      // temporarily show it
      this.settings.showReportBugFAB2 = true;
      await sleep(0); // angular needs a tick to update the DOM
    }

    this.fab?.nativeElement?.classList.add('rotating-element');

    // suspend scroll-hide detection during screenshot capture to prevent header state corruption
    this.scrollHideService.suspendUntilNextFrame();

    // capture the screenshot. how far each scrollable area is scrolled is
    // corrected inside the copy the capture draws, by buildBugReportCaptureOptions,
    // so nothing on the live page is moved here
    try {
      await sleep(500); // allow the animation to start, before the screenshot can freeze it
      const canvas = await domToImage.toCanvas(
        this.host.getCaptureRoot(),
        buildBugReportCaptureOptions({
          excludeElement: this.fab.nativeElement,
          height: window.innerHeight,
          width: window.innerWidth,
        })
      );
      this.screenshotTrip.recordCapture(canvas.toDataURL());
    } catch (error) {
      // log the error but continue to put the button and the header back
      console.error('Failed to capture screenshot:', error);
      // the user asked for a screenshot and is about to land back on the form
      // without one, so say so and report it. a console line alone reaches nobody
      // in production, and the silence used to read as the whole trip going wrong
      this.logging.trackSilentProblem('ScreenshotCaptureFailed', errorReportFields(error));
      this.toastCtrl.showToast({
        message: LL.app.screenshotCaptureFailed(),
        type: ToastType.WarningLight,
        duration: 5000,
      });
      // record that no image came back, so the feedback page knows the capture failed
      this.screenshotTrip.recordCapture(undefined);
    } finally {
      this.fab?.nativeElement?.classList.remove('rotating-element');

      // refresh scroll-hide service to reset header state after the capture
      this.scrollHideService.refresh();
    }

    // navigate to the return page (coach message or feedback page)
    const returnPage = this.screenshotTrip.takeReturnPage();
    if (returnPage) {
      await this.navCtrl.pushAsync(returnPage, {});
    } else {
      await this.navCtrl.pushAsync(ISendFeedbackPage, { sentryId: sentryId, type: this.sendFeedbackType });
      // the type belonged to this one report. left standing, a later plain tap on the camera
      // button would open an "Issue" report about no error at all (#3255)
      this.sendFeedbackType = undefined;
    }

    // if the button was not visible before, hide it again
    if (fabDidntExist) {
      this.settings.showReportBugFAB2 = false;
    }
  }

  shouldDisplayReportBugButton() {
    return shouldShowReportBugFab(this.navCtrl.getActivePageName(), this.settings.showReportBugFAB2);
  }

  dragStart(e: MouseEvent | TouchEvent) {
    this.dragging = true;
    this.moved = false;
    const el = this.fab.nativeElement;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    this.dragOrgX = clientX;
    this.dragOrgY = clientY;
    const rect = el.getBoundingClientRect();
    this.offsetX = clientX - rect.left;
    this.offsetY = clientY - rect.top;
    this.dragInsets = { top: readSafeAreaInset(document, 'top'), bottom: readSafeAreaInset(document, 'bottom') };
    // Switch to fixed positioning with explicit coordinates
    el.style.position = 'fixed';
    el.style.left = `${rect.left}px`;
    el.style.top = `${rect.top}px`;
    el.style.bottom = 'auto';
    el.classList.add('dragged-element');
    e.preventDefault();
    e.stopPropagation();
  }

  dragMove(e: MouseEvent | TouchEvent) {
    if (!this.dragging) {
      return;
    }
    const el = this.fab.nativeElement;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    // if didn't move yet, discard small movements when detecting whether it's a click or drag
    if (!this.moved && hasBugFabDragStarted({ x: this.dragOrgX, y: this.dragOrgY }, { x: clientX, y: clientY })) {
      this.moved = true;
    }

    // clamp position so that fab doesn't get out of screen, accounting for fab size
    const position = clampBugFabPosition(
      { x: clientX - this.offsetX, y: clientY - this.offsetY },
      el.offsetWidth,
      { width: window.innerWidth, height: window.innerHeight },
      this.dragInsets,
    );

    el.style.left = `${position.x}px`;
    el.style.top = `${position.y}px`;

    el.style.position = `fixed`;
    e.preventDefault();
    e.stopPropagation();
  }

  dragEnd(e: MouseEvent | TouchEvent) {
    void e;
    if (!this.dragging) {
      return;
    }
    this.dragging = false;
    this.fab.nativeElement.classList.remove('dragged-element');
  }
}
